// The AI Champions Cup: communities race on points for what their people add
// to AI Initiatives and the AI Library this quarter. The full standings are on
// the Champions page; the dashboard shows the viewer where their community is.
import { useEffect, useMemo, useState } from "react";
import { Trophy } from "lucide-react";
import { useContributions } from "../../hooks/useContributions";
import { POINTS, quarterStart, scoreCommunities, type CommunityScore, type Exclusions } from "../../lib/contributions";
import type { Community, CommunityId } from "../../data/championsDirectory";
import { COMMUNITY_ICONS } from "./parts";

export type CupStandings = { scores: CommunityScore[]; label: string } | null;

const quarterLabel = () => `Q${Math.floor(new Date().getMonth() / 3) + 1} ${new Date().getFullYear()}`;

/** This quarter's standings (or all time, if nothing has been added yet this
 *  quarter), leaving out anyone excluded from the leaderboards. */
export function useCupStandings(communities: Community[], exclude: Exclusions): CupStandings {
  const { data } = useContributions();
  return useMemo(() => {
    if (!data) return null;
    const ids = communities.map((c) => c.id);
    const q = scoreCommunities(data.initiatives, data.library, ids, quarterStart(), exclude);
    const quiet = q.every((s) => !s.points);
    return {
      scores: quiet ? scoreCommunities(data.initiatives, data.library, ids, null, exclude) : q,
      label: quiet ? "All time" : quarterLabel(),
    };
  }, [data, communities, exclude]);
}

/** Where a community stands: its rank and, unless it leads, the community
 *  just ahead and what it would take to pass it. */
export function standingOf(cup: CupStandings, mine: CommunityId | null) {
  if (!cup || !mine) return null;
  const rank = cup.scores.findIndex((s) => s.id === mine);
  if (rank < 0) return null;
  const ahead = rank > 0 ? cup.scores[rank - 1] : null;
  const gap = ahead ? ahead.points - cup.scores[rank].points + 1 : 0;
  return { rank: rank + 1, of: cup.scores.length, points: cup.scores[rank].points, ahead: ahead?.id ?? null, gap, ideas: Math.ceil(gap / POINTS.idea) };
}

/** One line on where a community stands, and what would move it up. */
export function standingLine(cup: CupStandings, mine: CommunityId | null, nameOf: (id: CommunityId) => string): string {
  if (!cup) return "Tallying this quarter's points…";
  const rank = mine ? cup.scores.findIndex((s) => s.id === mine) : -1;
  if (rank < 0) return "Every idea, prompt and agent you add earns points for your team.";
  if (rank === 0) return `${nameOf(mine!)} leads the Champions Cup. Keep it up!`;
  const gap = cup.scores[rank - 1].points - cup.scores[rank].points + 1;
  const ideas = Math.ceil(gap / POINTS.idea);
  return `${nameOf(mine!)} is #${rank + 1} in the Champions Cup, ${gap} pt${gap === 1 ? "" : "s"} from passing ${nameOf(
    cup.scores[rank - 1].id
  )}. That's ${ideas} idea${ideas === 1 ? "" : "s"}.`;
}

/** The standings section on the Champions page. */
export function ChampionsCup({
  communities,
  exclude,
  mine,
  onHowPoints,
}: {
  communities: Community[];
  exclude: Exclusions;
  mine: CommunityId | null;
  onHowPoints: () => void;
}) {
  const cup = useCupStandings(communities, exclude);
  // Bars grow in once the scores arrive.
  const [grow, setGrow] = useState(false);
  useEffect(() => {
    if (!cup) return;
    const t = setTimeout(() => setGrow(true), 60);
    return () => clearTimeout(t);
  }, [cup]);

  const nameOf = (id: CommunityId) => communities.find((c) => c.id === id)?.name ?? id;
  const scores = cup?.scores ?? communities.map((c) => ({ id: c.id, points: 0, items: 0 }));
  const best = Math.max(1, ...scores.map((s) => s.points));

  return (
    <section id="champions-cup" className="db-card cup" aria-labelledby="cup-title">
      <div className="cup-head">
        <span className="cup-trophy" aria-hidden>
          <Trophy size={20} />
        </span>
        <div className="cup-head-text">
          <p className="ch-kicker">AI Champions Cup · {cup?.label ?? quarterLabel()}</p>
          <h2 id="cup-title" className="cup-title">
            Which community is doing the most with AI?
          </h2>
        </div>
        <button type="button" className="tc-guide-btn" onClick={onHowPoints}>
          How points work
        </button>
      </div>
      <ol className="cup-board" aria-label={`Standings, ${cup?.label ?? quarterLabel()}`}>
        {scores.map((s, i) => {
          const Icon = COMMUNITY_ICONS[s.id] ?? Trophy;
          const you = s.id === mine;
          return (
            <li key={s.id} className={`cup-row ch-t-${s.id}${you ? " is-mine" : ""}`}>
              <span className={`cup-rank cup-rank--${i < 3 && cup ? i + 1 : "n"}`}>{i + 1}</span>
              <span className="cup-icon" aria-hidden>
                <Icon size={14} />
              </span>
              <span className="cup-name">
                {nameOf(s.id)}
                {you ? <span className="cup-you">You</span> : null}
              </span>
              <span className="cup-bar" aria-hidden>
                <span style={{ width: grow ? `${Math.max(3, (s.points / best) * 100)}%` : "0%" }} />
              </span>
              <span className="cup-pts">{cup ? s.points : "–"}</span>
            </li>
          );
        })}
      </ol>
      <p className="cup-foot">
        {standingLine(cup, mine, nameOf)} Points come from what each community's people add to AI Initiatives and the AI
        Library, and start fresh every quarter.
      </p>
    </section>
  );
}
